import {
  Component,
  Input,
  forwardRef,
  ViewChild,
  AfterViewInit,
  OnChanges,
  ElementRef,
  EventEmitter,
  Output,
  HostListener
} from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'rms-textarea',
  templateUrl: './textarea-field.component.html',
  styleUrls: ['./textarea-field.component.scss', '../rms-styles.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaFieldComponent),
      multi: true
    }
  ],
  imports: [CommonModule, FormsModule, MatIconModule, MatButtonModule]
})
export class TextareaFieldComponent implements ControlValueAccessor, AfterViewInit, OnChanges {
  @Input() label: string = '';
  @Input() placeholder: string = this.label;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() focusOnLoad: boolean = false;

  @Input() includeSave: boolean = false;
  @Input() buttonClass: 'btn-bg-green' | 'btn-bg-blue' | 'btn-bg-red' = 'btn-bg-green';
  @Input() buttonLabel: string = 'Save';

  /**
   * Controls browser textarea resizing.
   *
   * none       = locked width and height
   * vertical   = height only
   * horizontal = width only
   * both       = width and height
   */
  @Input() resize: 'none' | 'vertical' | 'horizontal' | 'both' = 'none';

  /**
   * Shows a popout editor button.
   */
  @Input() includePopout: boolean = true;
  @Input() popoutTabIndex: number = 0;

  /**
   * Optional title for popout modal.
   * Falls back to label if not provided.
   */
  @Input() popoutTitle: string | null = null;

  /**
   * If true, popout textarea is read-only.
   * Useful for long notes/descriptions you only want users to view.
   */
  @Input() popoutReadonly: boolean = false;

  @Output() onSave = new EventEmitter<any>();

  @ViewChild('nativeTextArea', { read: ElementRef })
  nativeTextArea?: ElementRef<HTMLTextAreaElement>;

  @ViewChild('popoutTextArea', { read: ElementRef })
  popoutTextArea?: ElementRef<HTMLTextAreaElement>;

  private pointerFocus = false;
  onPointerDown() { this.pointerFocus = true; }
  onBlur() { this.pointerFocus = false; this.onTouched(); }
  onFocus(event: FocusEvent) {
    const textarea = event.target as HTMLTextAreaElement;
    if (!this.pointerFocus) textarea.select();
    this.pointerFocus = false;
  }

  value: any = '';

  popoutOpen = false;
  popoutValue: any = '';

  onChange = (_: any) => { };
  onTouched = () => { };

  ngAfterViewInit(): void {
    this.tryFocus();
  }

  ngOnChanges(changes: any): void {
    if (changes['focusOnLoad']) {
      queueMicrotask(() => this.tryFocus());
    }
  }

  writeValue(value: any): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public focus(): void {
    this.tryFocus(true);
  }

  private tryFocus(force = false): void {
    if ((!this.focusOnLoad && !force) || this.disabled) return;

    setTimeout(() => {
      const el = this.nativeTextArea?.nativeElement;
      if (!el) return;

      el.focus({ preventScroll: true });

      try {
        const len = el.value?.length ?? 0;
        el.setSelectionRange(len, len);
      } catch { }
    }, 0);
  }

  handleMainValueChange(value: any): void {
    this.value = value;
    this.onChange(this.value);
  }

  onSaveClicked(): void {
    this.onSave.emit(this.value);
  }

  openPopout(): void {
    if (this.disabled && !this.value) return;

    this.popoutValue = this.value ?? '';
    this.popoutOpen = true;

    setTimeout(() => {
      const el = this.popoutTextArea?.nativeElement;
      if (!el) return;

      el.focus({ preventScroll: true });

      try {
        const len = el.value?.length ?? 0;
        el.setSelectionRange(len, len);
      } catch { }
    }, 0);
  }

  closePopout(): void {
    this.popoutOpen = false;
    this.popoutValue = '';
  }

  applyPopoutValue(): void {
    if (!this.popoutReadonly && !this.disabled) {
      this.value = this.popoutValue ?? '';
      this.onChange(this.value);
      this.onTouched();
    }

    this.closePopout();
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (this.popoutOpen) {
      this.closePopout();
    }
  }
}
